import { useRef, useState } from "react";
import { useNotify, useRefresh, useTranslate, type Identifier } from "ra-core";
import { useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import {
  aiNoteExtractEnabled,
  applyNoteProposal,
  proposalErrorText,
  rejectNoteProposal,
  requestNoteProposal,
  type NextActionChoice,
  type NoteProposal,
} from "./aiNoteExtract";
import { formatProposalDue } from "./noteDue";

export function AiNoteExtractButton({
  contactId,
  text,
}: {
  contactId: Identifier;
  /** Saved note text. Omit inside the note form; the field is read from there. */
  text?: string;
}) {
  if (!aiNoteExtractEnabled) return null;
  if (text != null) {
    return <AiNoteExtractControl contactId={contactId} text={text} />;
  }
  return <AiNoteExtractFromForm contactId={contactId} />;
}

function AiNoteExtractFromForm({ contactId }: { contactId: Identifier }) {
  const value = useWatch({ name: "text" });
  const text = typeof value === "string" ? value : "";
  return <AiNoteExtractControl contactId={contactId} text={text} />;
}

export function AiNoteExtractControl({
  contactId,
  text,
}: {
  contactId: Identifier;
  text: string;
}) {
  const notify = useNotify();
  const translate = useTranslate();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposal, setProposal] = useState<NoteProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusOn, setStatusOn] = useState(true);
  const [titleOn, setTitleOn] = useState(true);
  const [nextOn, setNextOn] = useState(true);
  const [choice, setChoice] = useState<NextActionChoice | undefined>(undefined);
  const decided = useRef(false);

  const trimmed = text.trim();
  const canAsk = trimmed.length >= 3 && !loading;

  const ask = async () => {
    decided.current = false;
    setOpen(true);
    setLoading(true);
    setError(null);
    setProposal(null);
    setChoice(undefined);
    setStatusOn(true);
    setTitleOn(true);
    setNextOn(true);
    try {
      const { proposal: next } = await requestNoteProposal(contactId, trimmed);
      setProposal(next);
    } catch (caught) {
      const code = caught instanceof Error ? caught.message : "request_failed";
      setError(proposalErrorText(code));
    } finally {
      setLoading(false);
    }
  };

  const closeWithoutDecision = () => {
    setOpen(false);
    setProposal(null);
    setError(null);
  };

  const cancel = async () => {
    if (decided.current) {
      closeWithoutDecision();
      return;
    }
    decided.current = true;
    const current = proposal;
    closeWithoutDecision();
    if (!current) return;
    try {
      await rejectNoteProposal(contactId, current.proposal_id);
    } catch {
      notify("palmtree.notes.reject_not_saved", { type: "warning" });
    }
  };

  const statusChange = proposal?.changes.find(
    (change) => change.field === "status",
  );
  const titleChange = proposal?.changes.find(
    (change) => change.field === "title",
  );
  const choiceRequired = Boolean(
    proposal?.next_action && proposal.existing_next_action && nextOn,
  );
  const selectedCount =
    (statusChange && statusOn ? 1 : 0) +
    (titleChange && titleOn ? 1 : 0) +
    (proposal?.next_action && nextOn ? 1 : 0);
  const canApply =
    Boolean(proposal) &&
    selectedCount > 0 &&
    !saving &&
    (!choiceRequired || choice != null);

  const apply = async () => {
    if (!proposal || !canApply) return;
    setSaving(true);
    try {
      const result = await applyNoteProposal({
        contactId,
        proposalId: proposal.proposal_id,
        fields: {
          status: Boolean(statusChange) && statusOn,
          title: Boolean(titleChange) && titleOn,
        },
        nextAction: Boolean(proposal.next_action) && nextOn,
        nextActionChoice: choice || undefined,
      });
      notify(
        result.decision === "partially_applied"
          ? "Een deel van het voorstel is toegepast"
          : "Voorstel toegepast",
      );
      decided.current = true;
      closeWithoutDecision();
      refresh();
    } catch (caught) {
      const code = caught instanceof Error ? caught.message : "request_failed";
      // The server already stored this rejection. Annuleren must only close.
      if (code === "status_not_allowed") decided.current = true;
      setError(proposalErrorText(code));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!canAsk}
        onClick={ask}
      >
        {translate("palmtree.notes.ask")}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) void cancel();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate("palmtree.notes.title")}</DialogTitle>
            <DialogDescription>
              {translate("palmtree.notes.description")}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <p className="text-sm text-muted-foreground">
              {translate("palmtree.notes.loading")}
            </p>
          ) : null}

          {error ? (
            <p className="text-sm text-warn">{translate(error)}</p>
          ) : null}

          {proposal ? (
            <div className="space-y-4 text-sm">
              {proposal.summary ? (
                <p>{proposal.summary}</p>
              ) : (
                <p className="text-muted-foreground">
                  {translate("palmtree.notes.no_summary")}
                </p>
              )}

              <div className="space-y-2">
                <h3 className="font-medium">
                  {translate("palmtree.notes.changes")}
                </h3>
                {!statusChange && !titleChange ? (
                  <p className="text-muted-foreground">
                    {translate("palmtree.notes.no_field_changes")}
                  </p>
                ) : null}
                {statusChange ? (
                  <ChangeRow
                    id="ai-status"
                    label={translate("palmtree.notes.field_status")}
                    from={statusChange.from}
                    to={statusChange.to}
                    quote={statusChange.quote}
                    checked={statusOn}
                    onCheckedChange={setStatusOn}
                  />
                ) : null}
                {titleChange ? (
                  <ChangeRow
                    id="ai-title"
                    label={translate("palmtree.notes.field_role")}
                    from={titleChange.from}
                    to={titleChange.to}
                    checked={titleOn}
                    onCheckedChange={setTitleOn}
                  />
                ) : null}
              </div>

              <div className="space-y-2">
                <h3 className="font-medium">
                  {translate("palmtree.notes.next_action")}
                </h3>
                {proposal.next_action ? (
                  <label className="flex items-start gap-3" htmlFor="ai-next">
                    <Checkbox
                      id="ai-next"
                      checked={nextOn}
                      onCheckedChange={(value) => setNextOn(value === true)}
                    />
                    <span>
                      {proposal.next_action.title}
                      <span className="mt-0.5 block text-muted-foreground">
                        {formatProposalDue(
                          proposal.next_action.due_date,
                          proposal.next_action.due_time ?? null,
                          translate("palmtree.notes.no_due"),
                        )}
                      </span>
                    </span>
                  </label>
                ) : (
                  <p className="text-muted-foreground">
                    {translate("palmtree.notes.no_next")}
                  </p>
                )}
                {choiceRequired && proposal.existing_next_action ? (
                  <div className="space-y-2 rounded-md border p-3">
                    <p>
                      {translate("palmtree.notes.existing")}{" "}
                      <span className="font-medium">
                        {proposal.existing_next_action.title}
                      </span>
                    </p>
                    <RadioGroup
                      value={choice ?? ""}
                      onValueChange={(value) =>
                        setChoice(value as NextActionChoice)
                      }
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem
                          value="replace"
                          id="ai-choice-replace"
                        />
                        <Label
                          htmlFor="ai-choice-replace"
                          className="font-normal text-foreground"
                        >
                          {translate("palmtree.notes.replace")}
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem
                          value="complete_and_create"
                          id="ai-choice-complete"
                        />
                        <Label
                          htmlFor="ai-choice-complete"
                          className="font-normal text-foreground"
                        >
                          {translate("palmtree.notes.complete_and_create")}
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => void cancel()}
            >
              {translate("palmtree.notes.cancel")}
            </Button>
            <Button
              type="button"
              disabled={!canApply}
              onClick={() => void apply()}
            >
              {saving
                ? translate("palmtree.notes.saving")
                : translate("palmtree.notes.apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ChangeRow({
  id,
  label,
  from,
  to,
  quote,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  from: string | null;
  to: string;
  quote?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3" htmlFor={id}>
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <span>
        <span className="font-medium">{label}</span>
        <span className="mt-0.5 block text-muted-foreground">
          {from || "—"} → {to}
        </span>
        {quote ? (
          <span className="mt-0.5 block text-muted-foreground">“{quote}”</span>
        ) : null}
      </span>
    </label>
  );
}
